import { Textarea } from '@today/ui-primitives'
import { type ReactElement, useId } from 'react'

/** The view's built-in copy; override any entry for localisation. */
export interface OfferMessageStepLabels {
	message: string
	messageHint: string
	language: string
	preview: string
	previewFrame: string
	previewLoading: string
}

const DEFAULT_LABELS: OfferMessageStepLabels = {
	message: 'Message',
	messageHint: 'A short personal note. Supports **bold**, *italic*, lists and https links.',
	language: 'Language',
	preview: 'Customer preview',
	previewFrame: 'Landing preview',
	previewLoading: 'Rendering preview…',
}

const SELECT =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text disabled:cursor-not-allowed disabled:opacity-50'

/**
 * Props for {@link OfferMessageStepView}.
 *
 * @property message - The message being edited (unsaved edits included).
 * @property maxLength - The most characters the editor accepts.
 * @property locale - The selected locale tag.
 * @property localeOptions - The locale tags offered, in display order.
 * @property readOnly - True when the offer can no longer be edited; disables the editor and the switch.
 * @property saveStatus - The save indicator text (saving / saved / refused), or nothing.
 * @property previewHtml - The rendered landing document; `null` while the first preview renders.
 * @property previewError - A refused-preview message shown in place of the preview.
 */
export interface OfferMessageStepViewProps {
	message: string
	maxLength: number
	locale: string
	localeOptions: readonly string[]
	readOnly: boolean
	saveStatus: string | null
	previewHtml: string | null
	previewError: string | null
	onMessageChange: (message: string) => void
	onLocaleChange: (locale: string) => void
	labels?: Partial<OfferMessageStepLabels>
}

/** The preview pane: the sandboxed landing, the refused-preview message, or the loading text. */
function PreviewPane(props: {
	html: string | null
	error: string | null
	labels: OfferMessageStepLabels
}): ReactElement {
	const { html, error, labels } = props
	if (error) {
		return (
			<p role="alert" className="text-ds-sm text-ds-danger">
				{error}
			</p>
		)
	}
	if (html === null) {
		return (
			<p className="text-ds-sm text-ds-text-muted" aria-live="polite">
				{labels.previewLoading}
			</p>
		)
	}
	// `sandbox=""` applies every restriction: the landing runs no script and gets no
	// same-origin access to the backoffice (TO-124 Security).
	return (
		<iframe
			title={labels.previewFrame}
			sandbox=""
			srcDoc={html}
			className="h-96 w-full rounded-ds-md border border-ds-border bg-ds-surface"
		/>
	)
}

/** The locale switch: the offered tags, disabled when read-only. */
function LocaleSwitch(props: OfferMessageStepViewProps & { labels: OfferMessageStepLabels }): ReactElement {
	const localeId = useId()
	return (
		<>
			<label htmlFor={localeId} className="text-ds-sm font-medium text-ds-text">
				{props.labels.language}
			</label>
			<select
				id={localeId}
				className={SELECT}
				value={props.locale}
				disabled={props.readOnly}
				onChange={(event) => props.onLocaleChange(event.target.value)}
			>
				{props.localeOptions.map((tag) => (
					<option key={tag} value={tag}>
						{tag}
					</option>
				))}
			</select>
		</>
	)
}

/** The message editor with its hint, the `n / max` counter and the save status. */
function MessageEditor(props: OfferMessageStepViewProps & { labels: OfferMessageStepLabels }): ReactElement {
	const messageId = useId()
	const hintId = useId()
	return (
		<>
			<label htmlFor={messageId} className="text-ds-sm font-medium text-ds-text">
				{props.labels.message}
			</label>
			<Textarea
				id={messageId}
				aria-describedby={hintId}
				value={props.message}
				maxLength={props.maxLength}
				disabled={props.readOnly}
				onChange={(event) => props.onMessageChange(event.target.value)}
			/>
			<p id={hintId} className="flex justify-between gap-3 text-ds-xs text-ds-text-muted">
				<span>{props.labels.messageHint}</span>
				<span>
					{props.message.length} / {props.maxLength}
				</span>
			</p>
			{props.saveStatus ? (
				<p aria-live="polite" className="text-ds-sm text-ds-text-muted">
					{props.saveStatus}
				</p>
			) : null}
		</>
	)
}

/**
 * Offer Wizard Step 3 (TO-124): the message editor and the locale switch on the
 * left, the live landing preview on the right. Pure: the text, the options, the
 * save status, the rendered html and the callbacks are props; it neither
 * debounces, saves nor fetches anything.
 */
export function OfferMessageStepView(props: OfferMessageStepViewProps): ReactElement {
	const labels = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<div className="grid gap-6 md:grid-cols-2">
			<section className="flex flex-col gap-3">
				<LocaleSwitch {...props} labels={labels} />
				<MessageEditor {...props} labels={labels} />
			</section>
			<section aria-label={labels.preview} className="flex flex-col gap-3">
				<PreviewPane html={props.previewHtml} error={props.previewError} labels={labels} />
			</section>
		</div>
	)
}
